/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { getAttributesForm } from '@/auth/api';
import { useEffect, useState } from 'preact/hooks';
import { inputsDefaultValueSetProps } from '@/auth/types';
import { DEFAULT_SIGN_UP_FIELDS } from '@/auth/configs/defaultCreateUserConfigs';
import { AttributesFormItemsProps } from '@/auth/data/models';
import { simplifyTransformAttributesForm } from '@/auth/lib/simplifyTransformAttributesForm';

interface UseGetAttributesFormProps {
  fieldsConfigForApiVersion1: {}[];
  apiVersion2: boolean;
  inputsDefaultValueSet?: inputsDefaultValueSetProps[];
}

const applyDefaultValuesToFields = (
  fields: AttributesFormItemsProps[],
  defaultValues: inputsDefaultValueSetProps[] | any
) => {
  if (!defaultValues?.length) return fields;

  return fields.map((el) => {
    const defaultValue = defaultValues.find(
      ({ code }: inputsDefaultValueSetProps) => code === el.code
    )?.defaultValue;

    return defaultValue ? { ...el, defaultValue } : el;
  });
};

export const useGetAttributesForm = ({
  inputsDefaultValueSet,
  fieldsConfigForApiVersion1,
  apiVersion2,
}: UseGetAttributesFormProps) => {
  const [fieldsListConfigs, setFieldsListConfigs] = useState<
    AttributesFormItemsProps[]
  >([]);

  useEffect(() => {
    const fetchFieldsConfig = async () => {
      if (apiVersion2) {
        const response = await getAttributesForm('customer_account_create');

        if (response?.length) {
          if (inputsDefaultValueSet?.length) {
            const fieldsWithDefaultValues: any = applyDefaultValuesToFields(
              response,
              inputsDefaultValueSet
            );

            setFieldsListConfigs(fieldsWithDefaultValues);
          } else {
            setFieldsListConfigs(response);
          }
        }
      } else {
        const transformAttributesFields = simplifyTransformAttributesForm(
          DEFAULT_SIGN_UP_FIELDS
        );
        const transformFieldsConfigForApiVersion1 =
          simplifyTransformAttributesForm(fieldsConfigForApiVersion1);

        const defaultFieldsWithDefaultValues: any = applyDefaultValuesToFields(
          transformAttributesFields as AttributesFormItemsProps[],
          inputsDefaultValueSet
        );

        setFieldsListConfigs(
          fieldsConfigForApiVersion1 && fieldsConfigForApiVersion1.length
            ? transformFieldsConfigForApiVersion1
            : defaultFieldsWithDefaultValues
        );
      }
    };

    fetchFieldsConfig();
  }, [apiVersion2, fieldsConfigForApiVersion1, inputsDefaultValueSet]);

  return {
    fieldsListConfigs: fieldsListConfigs.map((element) => ({
      ...element,
      ...(element.code === 'email' ? { autocomplete: 'username' } : {}),
    })),
  };
};
